import {Component} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    OrganizationApiService,
    OrganizationResponse,
    RoleApiService,
    RolesDictionaryResponse,
    UserActivationApiService,
    UserApiService,
    UserAttributesApiService,
    UserCredentialDeliveryStatus,
    UserResponse,
} from '@npck/af-frontend-api';
import {formatDateTimeFromServerToUi, LocalizationService, RouterService, rxSpinner} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {EMPTY, mergeMap, of, zip} from 'rxjs';
import {PasswordExpiryStatus, PasswordExpiryStatusService} from '../../../../domain/password-expiry-status.service';
import {formatPhone} from '../../../../domain/phone-utils';
import {ORGANIZATION_ID, USER_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {FieldInfo} from '../../../subjects/details/subject-details.component';
import {AdminPanelTab} from '../../admin.component';

const DATE_TIME_FIELDS: string[] = ['lastLoginDateTime', 'createdDate', 'deletedDate', 'credentialDeliveryErrorDateTime'];
const PASSWORD_EXPIRY_FIELD = 'passwordExpiryDaysLeft';
const PASSWORD_EXPIRY_FIELD_ANCHOR = 'tag';

@Component({
    templateUrl: 'user-details.component.html',
    styleUrls: ['user-details.component.scss'],
    standalone: false
})
export class UserDetailsComponent {
    userResponse: UserResponse;
    userId: string;
    organizationId: string;
    organization?: OrganizationResponse;
    informationFields: FieldInfo[];
    accessFields: FieldInfo[];
    passwordExpiryStatus: PasswordExpiryStatus;
    readonly passwordExpiryField = PASSWORD_EXPIRY_FIELD;
    commonInformationFields: (keyof UserResponse)[] = [
        'lastName',
        'firstName',
        'iin',
        'username',
        'email',
        'phoneNumber',
        'organizationType',
        'bin',
        'credentialDeliveryStatus',
        'credentialErrorReason',
        'credentialDeliveryErrorDateTime',
    ];
    accessInformationFields: (keyof UserResponse)[] = ['tag', 'dn', 'emailNotify'];
    additionalAccessInformationFields: (keyof RolesDictionaryResponse)[] = ['composite'];
    metaInformationFields: (keyof UserResponse)[] =
        ['lastLoginDateTime', 'createdBy', 'createdDate', 'deletedBy', 'deletedDate', 'deactivationReason'];
    tabPathToTab: Record<string, AdminPanelTab> = {[Path.USERS]: 'tabUsers'};

    constructor(
        private activatedRoute: ActivatedRoute,
        private userApiService: UserApiService,
        private userActivationApiService: UserActivationApiService,
        private userAttributesApiService: UserAttributesApiService,
        private roleApiService: RoleApiService,
        private organizationApiService: OrganizationApiService,
        private routerService: RouterService,
        private localizationService: LocalizationService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private passwordExpiryStatusService: PasswordExpiryStatusService,
    ) {
        this.userId = this.activatedRoute.snapshot.paramMap.get(USER_ID);
        this.organizationId = this.activatedRoute.snapshot.paramMap.get(ORGANIZATION_ID);
        if (this.organizationId) {
            this.tabPathToTab = {[Path.ORGANIZATIONS]: 'tabOrganizations'};
            this.organizationApiService.getOrganization(this.organizationId)
                .subscribe(organization => this.organization = organization);
        }
        userAttributesApiService.getUser(this.userId)
            .pipe(
                mergeMap(user => zip([
                    of(user),
                    this.roleApiService.getUserRoles(user.id),
                    this.roleApiService.getRolesDictionary()],
                )),
                rxSpinner('initial'),
            )
            .subscribe({
                next: ([user, roles, rolesDictionary]) => {
                    this.informationFields = this.mapFieldToValue(this.commonInformationFields, user, roles, rolesDictionary);
                    this.accessFields = this.mapFieldToValue([...this.accessInformationFields], user, roles, rolesDictionary);
                    this.accessFields.splice(
                        this.accessFields.findIndex(field => field.filedName === PASSWORD_EXPIRY_FIELD_ANCHOR) + 1,
                        0,
                        {filedName: PASSWORD_EXPIRY_FIELD, fieldValue: ''},
                    );
                    this.accessFields.push(
                        ...this.mapFieldToValue(this.additionalAccessInformationFields, user, roles, rolesDictionary));
                    this.accessFields.push(...this.mapFieldToValue(this.metaInformationFields, user, roles, rolesDictionary));
                    this.passwordExpiryStatus = this.passwordExpiryStatusService.status(user.passwordExpiryDaysLeft);
                    this.userResponse = user;
                },
                error: (err) => {
                    this.navigateToList();
                    throw err;
                },
            });
    }

    get canResendOtp(): boolean {
        return this.userResponse?.isDeleted === false
            && this.userResponse?.mustChangePassword === true;
    }

    get breadcrumbFilterPath(): string {
        return this.organizationId ? Path.USERS : this.userId;
    }

    get userBreadcrumb(): string {
        return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#UserInfo', {
            fullName: [this.userResponse?.lastName || '', this.userResponse?.firstName || ''].join(' '),
            email: this.userResponse?.email,
        });
    }

    filterUserIdPath = (path: string): boolean => {
        return path === this.userId;
    };

    breadcrumbCustomizer = (path: string): string => {
        if (path === this.organizationId) {
            return this.organization?.nameRu;
        }
        if (path === this.userId) {
            return this.userBreadcrumb;
        }
        return path;
    };

    resendOtp(): void {
        const fullName = [this.userResponse.lastName, this.userResponse.firstName].filter(n => !!n).join(' ');
        this.dialogService.openDialog({
            title: 'i18n_af_resendOtpDialogTitle',
            subtitle: 'i18n_af_resendOtpDialogSubtitle',
            buttonOkText: 'i18n_af_buttonSend',
            buttonCancelText: 'i18n_af_buttonCancel',
            translationParams: {
                fullName: fullName,
                login: this.userResponse.username,
            },
        }).pipe(
            mergeMap(result => !result && EMPTY || this.userApiService.sendUserOtp(this.userResponse.id).pipe(rxSpinner('resendingOtp'))),
        )
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackbarMessageOtpResent', 'success', false, {
                    fullName: fullName,
                    login: this.userResponse.username,
                });
            });
    }

    editUser(): void {
        this.routerService.navigateToTab<AdminPanelTab>(`${Path.ADMIN_PANEL_USERS_EDIT.replace(`:${USER_ID}`, this.userId)}`, 'tabUsers');
    }

    reactivate(): void {
        const fullName = [this.userResponse.lastName, this.userResponse.firstName].filter(n => !!n).join(' ');
        this.dialogService.openDialog({
            title: 'i18n_af_reactivateDialogTitle',
            subtitle: 'i18n_af_reactivateDialogSubtitle',
            buttonOkText: 'i18n_af_buttonConfirm',
            buttonCancelText: 'i18n_af_buttonCancel',
            translationParams: {
                fullName: fullName,
                login: this.userResponse.username,
            },
        }).pipe(
            mergeMap(result =>
                !result && EMPTY || this.userActivationApiService.reactivateUser(this.userResponse.id).pipe(rxSpinner('reactivating'))),
        )
            .subscribe(() => {
                this.userResponse.isDeleted = false;
                this.snackbarService.showSnackBar('i18n_af_snackbarMessageUserReactivated', 'success', false, {
                    fullName: fullName,
                    login: this.userResponse.username,
                });
            });
    }

    deleteUser(): void {
        this.dialogService.openDialog({
            title: 'i18n_af_userDeactivationDialogTitle',
            subtitle: 'i18n_af_userDeactivationDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDeactivate',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {
                fullName: [this.userResponse.lastName, this.userResponse.firstName].filter(n => !!n).join(' '),
                login: this.userResponse.username,
            },
        }).pipe(
            mergeMap(dialogResult => !dialogResult && EMPTY || this.userActivationApiService.deactivateUser(this.userResponse.id)
                .pipe(rxSpinner('deleting'))),
        )
            .subscribe(() => {
                this.navigateToList();
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageUserDeactivated', 'success', false, {
                    fullName: [this.userResponse.lastName, this.userResponse.firstName].filter(n => !!n).join(' '),
                    login: this.userResponse.username,
                });
            });
    }

    private navigateToList(): void {
        if (this.organizationId) {
            this.routerService.navigateToTab<AdminPanelTab>(
                `${Path.ADMIN_PANEL_ORGANIZATIONS}/${this.organizationId}`, 'tabOrganizations');
            return;
        }
        this.routerService.navigateToTab<AdminPanelTab>(Path.ADMIN_PANEL, 'tabUsers');
    }

    // TODO Move to frontend/projects/af/src/app/domain/field-builder.service.ts
    private mapFieldToValue(
        fields: FieldsType,
        userResponse: UserResponse,
        userRoles: string[],
        rolesDictionary: RolesDictionaryResponse,
    ): FieldInfo[] {
        return fields.map(field => {
            let value: any = (userResponse as any)[field];
            let showField = true;
            if (field === 'tag') {
                value = value && this.localizationService.getLocalizedKey(`i18n_af_userType#${value}`);
            } else if (field === 'dn') {
                value = value || '-';
            } else if (field === 'organizationType') {
                value = value && this.localizationService.getLocalizedKey(`i18n_af_organizationType#${value}`);
            } else if (field === 'composite') {
                value = userRoles.filter(role => rolesDictionary.composite.includes(role)).join(', ');
            } else if (field === 'emailNotify') {
                const translationPostfix = value ? 'enabled' : 'disabled';
                value = this.localizationService.getLocalizedKey(`i18n_af_notification#${translationPostfix}`);
            } else if (field === 'phoneNumber') {
                value = value && formatPhone(value);
            } else if (field === 'credentialDeliveryStatus') {
                value = value && this.localizationService.getLocalizedKey(
                    value === UserCredentialDeliveryStatus.FAILED ? 'i18n_af_credentialDeliveryFailed' : 'i18n_af_credentialDeliverySent');
            } else if (DATE_TIME_FIELDS.includes(field)) {
                if (field === 'credentialDeliveryErrorDateTime') {
                    showField = userResponse.credentialDeliveryStatus === UserCredentialDeliveryStatus.FAILED;
                }
                value = value && formatDateTimeFromServerToUi(dayjs(value));
            } else if (field === 'deactivationReason') {
                value = value && this.localizationService.getLocalizedKey(`i18n_af_deactivationReason#${value}`);
            } else if (field === 'credentialErrorReason') {
                showField = userResponse.credentialDeliveryStatus === UserCredentialDeliveryStatus.FAILED;
            }
            return {
                filedName: field,
                fieldValue: value,
                showField: showField,
            };
        })
            .filter(field => !!field.fieldValue);
    }
}

declare type FieldsType = (keyof UserResponse)[] | (keyof RolesDictionaryResponse)[];
